import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './CheckoutPage.module.css';
import CheckoutPaymentSection from './CheckoutPaymentSection';
import CheckoutDeliverySection from './CheckoutDeliverySection';
import CheckoutSummarySection from './CheckoutSummarySection';
import { UserAuthenticationStoreImplementation } from 'Data/UserAuthentication/UserAuthenticationStoreImplementation';
import { DeliveryAddressesStoreImplementation } from 'Data/DeliveryAddresses/DeliveryAddressesStoreImplementation';
import { OrderStoreImplementation } from 'Data/Order/OrderStoreImplementation';
import { UnauthorizedPopupStoreImplementation } from 'Data/UnauthorizedPopup/UnauthorizedPopupStoreImplementation';
import { OrderService } from 'Logic/OrderService';
import ConfirmModal from '../Modals/ConfirmModal';
import { useForceLogoutAndRedirect } from 'Logic/Functions/Hooks/useForceLogoutAndRedirect';
import { CartService } from 'Logic/CartService';
import { UserCartStoreImplementation } from 'Data/Cart/CartStoreImplementation';

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { authenticationState } = UserAuthenticationStoreImplementation();
  const { deliveryAddressesState } = DeliveryAddressesStoreImplementation();
  const { orderState } = OrderStoreImplementation();
  const { showPopup } = UnauthorizedPopupStoreImplementation();
  const onAuthError = useForceLogoutAndRedirect();
  const authToken = authenticationState?.token?.trim() ?? '';
  const isLoggedIn = !!authToken;
  const firstName = authenticationState?.user?.firstName ?? '';
  const lastName = authenticationState?.user?.lastName ?? '';
  const userName = `${firstName} ${lastName}`.trim() || 'User';
  const addressesLen = deliveryAddressesState?.items?.length ?? 0;
  const deliveryLoading = deliveryAddressesState?.loading ?? false;
  const disableCheckout = isLoggedIn && !deliveryLoading && addressesLen === 0;
  const items = orderState?.items ?? [];
  const selectedPaymentMethod = orderState?.selectedPaymentMethod ?? null;
  const selectedDeliveryAddress = orderState?.selectedDeliveryAddress ?? null;
  const selectedCarrier = orderState?.selectedDeliveryCarrier ?? null;

  const orderService = new OrderService();
  const cartService = new CartService();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const subtotal = useMemo(() => orderService.computeSubtotal(items), [items]);
  const deliveryFee = useMemo(() => Number(selectedCarrier?.fees ?? 0), [selectedCarrier?.fees]);
  const grandTotal = useMemo(() => subtotal + deliveryFee, [subtotal, deliveryFee]);
  const { removeManyFromCart } = UserCartStoreImplementation(
    'USD',
    cartService.mapCartStateToICartItems.bind(cartService),
    cartService.computeCartSubtotalFromICartItems.bind(cartService)
  );

  const itemsCount = useMemo(() => orderService.getItemsCount(items), [items]);

  const hasPayment = !!selectedPaymentMethod?.id;
  const hasAddress = !!selectedDeliveryAddress?.id;
  const hasCarrier = !!selectedCarrier?.id;

  const canPlaceOrder = useMemo(() => {
    return orderService.canPlaceOrder({
      disableCheckout,
      itemsCount,
      hasPayment,
      hasAddress,
      hasCarrier,
    });
  }, [disableCheckout, itemsCount, hasPayment, hasAddress, hasCarrier]);

  const confirmMessage = useMemo(() => {
    return orderService.buildConfirmMessage({ itemsCount, grandTotal });
  }, [itemsCount, grandTotal]);

  const openConfirm = useCallback(() => {
    orderService.handleOpenConfirm({
      disableCheckout,
      setApiError,
      setConfirmOpen,
    });
  }, [disableCheckout]);

  const handleConfirmPlaceOrder = useCallback(async () => {
    await orderService.handleAddOrder({
      token: authToken,
      items,
      selectedPaymentMethod,
      selectedDeliveryAddress,
      selectedCarrier,
      setLoading: setPlacingOrder,
      setApiError,
      onAuthError,
      showPopup,
      navigate,
      setConfirmOpen,
      removeOrderedItemsFromCart: cartService.deleteOrderedItemsFromBackend,
      removeManyFromCart
    });
  }, [
    orderService,
    cartService,
    authToken,
    items,
    selectedPaymentMethod,
    selectedDeliveryAddress,
    selectedCarrier,
    onAuthError,
    showPopup,
    navigate,
    setApiError,
  ]);

  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <main className={styles.page} data-testid="checkout-page">
      <header className={styles.header}>
        <h2 className={styles.title}>Checkout</h2>

        <p className={styles.greeting} data-testid="checkout-greeting">
          Almost there, <span className={styles.userName}>{userName}</span>
        </p>

        <p className={styles.subtitle}>Review your delivery details, payment, and order summary.</p>

        {apiError && (
          <div className={styles.error} data-testid="checkout-page-error">
            {apiError}
          </div>
        )}
      </header>

      <section className={styles.content}>
        <div className={styles.leftColumn}>
          <CheckoutDeliverySection />
          <CheckoutPaymentSection />
        </div>

        <aside className={styles.rightColumn}>
          <CheckoutSummarySection />
        </aside>
      </section>

      <footer className={styles.footer} data-testid="checkout-footer">
        <button
          type="button"
          className={styles.backButton}
          onClick={goBack}
          data-testid="checkout-back-button"
          disabled={placingOrder}
        >
          Back
        </button>

        <button
          type="button"
          className={styles.checkoutButton}
          onClick={openConfirm}
          data-testid="checkout-submit-button"
          disabled={!canPlaceOrder || placingOrder}
        >
          {placingOrder ? 'Placing…' : 'Place Order'}
        </button>
      </footer>

      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        message={confirmMessage}
        title="Confirm Order"
        confirmLabel={placingOrder ? 'Placing…' : 'Confirm Order'}
        cancelLabel="Cancel"
        onConfirm={handleConfirmPlaceOrder}
      />
    </main>
  );
};

export default CheckoutPage;
